import { useCallback } from "react";

import { TemplateDefinition } from "@assets/templates";
import { analytics, ANALYTICS_EVENTS } from "@demo/libs/analytics";
import { applyDetectExample } from "@demo/libs/detect";
import { insertTemplateIntoDocument } from "@demo/utils/document";

/**
 * The standard "user picked a template" flow: apply it to the active mode's
 * surface, fire analytics, then notify the caller. Used by both the inline
 * preview and the full examples sheet so they stay in sync if the flow evolves.
 */
export function useTemplateSelect(onTemplateSelected: () => void) {
  return useCallback(
    (template: TemplateDefinition) => {
      switch (template.mode) {
        case "compose":
          insertTemplateIntoDocument(template);
          break;
        case "detect":
          // Categories + config land synchronously; the image loads in the background.
          void applyDetectExample(template);
          break;
        default:
          assertNeverMode(template);
      }
      analytics.track(ANALYTICS_EVENTS.TEMPLATE_SELECTED, { template });
      onTemplateSelected();
    },
    [onTemplateSelected],
  );
}

/** Compile-time exhaustiveness guard: a newly added mode must be handled above. */
function assertNeverMode(template: never): never {
  throw new Error(`useTemplateSelect: unhandled template mode for ${JSON.stringify(template)}`);
}
